import React, { lazy, Suspense } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import "@shopify/polaris/build/esm/styles.css";
import "./index.css";
import enTranslations from "@shopify/polaris/locales/en.json";
import { AppProvider as PolarisProvider, Frame } from "@shopify/polaris";
import { Provider } from "react-redux";
import { store, persistor } from "./Reducers";
import Loader from "./Components/Panel/Loader/Loader";
import NavMenuWrapper from "./Components/NavMenuWrapper";
import { isAdminLoggedIn } from "./Components/Panel/Panel/index";
import * as Sentry from "@sentry/react";
import { PersistGate } from 'redux-persist/integration/react';

export const StoreDispatcher = React.createContext(store.dispatch);

const App = lazy(() => import(/* webpackChunkName: "app" */ "./App"));

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

const pathname = window.location.pathname;
const pathSegments = pathname.split("/");
const userId = pathSegments[2];

const isEnableSentry = (USER_ID: string = "") => {
  if (process.env.REACT_APP_SENTRY !== "1") {
    return false;
  }
  const users = process.env.REACT_APP_SENTRY_USER_ENABLE?.split(",") || [];
  if (users.length === 0) {
    return true;
  }
  return users.includes(USER_ID);
};

const sentryOptions = {
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment: process.env.REACT_APP_SENTRY_MODE,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
  // Session Replay
  replaysSessionSampleRate: 0.0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate:
    process.env.REACT_APP_SENTRY_MODE === "production" ? 1.0 : 0.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
};

if (isEnableSentry(userId) && !isAdminLoggedIn()) {
  Sentry.init(sentryOptions);
}

// Clean up legacy persist key if present to avoid stale rehydrate
try {
  sessionStorage.removeItem('persist:necessaryInfo');
} catch {}

root.render(
  <BrowserRouter>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <PolarisProvider i18n={enTranslations}>
          <StoreDispatcher.Provider value={store.dispatch}>
            <Frame>
              <NavMenuWrapper />
              <Suspense fallback={null}>
                <App />
              </Suspense>
            </Frame>
          </StoreDispatcher.Provider>
        </PolarisProvider>
      </PersistGate>
    </Provider>
  </BrowserRouter>
);
